import { useCallback, useEffect, useState, type ReactNode } from "react";
import { PenLine, X } from "lucide-react";
import { detectPendingSignature } from "../../lib/signing/detect-pending-sign";
import {
  fetchGatewayView,
  markGatewaySeen,
} from "../../lib/signing/gateway-api";
import {
  actOnPlatformSignature,
  requestPlatformSignReauth,
  verifyPlatformSignReauth,
} from "../../lib/signing/platform-sign-api";
import type { GatewayView, ReaderSigningSession } from "../../lib/signing/types";
import { SigningAuthPanel, SigningPanelHeader } from "./SigningAuthPanel";
import { CertUnlockPanel } from "../esign/CertUnlockPanel";
import { canCertSignFromLocal } from "../../lib/esign/local-store";
import { fetchEsignProfile } from "../../lib/esign/api";
import {
  loadBrowserCertificate,
  signWithBrowserCertificate,
} from "../../lib/esign/browser-cert-store";
import { useAuthStore } from "../../stores/auth-store";

type Props = {
  apiBase: string;
  contractId: string;
  session: ReaderSigningSession;
  mobileSheetOpen: boolean;
  onMobileSheetOpenChange: (open: boolean) => void;
  onSessionChange: (session: ReaderSigningSession | null) => void;
  onComplete?: () => void;
};

function useIsDesktop(): boolean {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 840px)").matches
      : true,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 840px)");
    const onChange = () => setDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

export function SigningOverlay({
  apiBase,
  contractId,
  session,
  mobileSheetOpen,
  onMobileSheetOpenChange,
  onSessionChange,
  onComplete,
}: Props) {
  const isDesktop = useIsDesktop();
  const userId = useAuthStore((s) => s.user?.id);
  const [gatewayView, setGatewayView] = useState<GatewayView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [reauthCode, setReauthCode] = useState("");
  const [reauthDebug, setReauthDebug] = useState<string | null>(null);
  const [reauthToken, setReauthToken] = useState<string | null>(null);
  const [platformDone, setPlatformDone] = useState(false);
  const [certSignEligible, setCertSignEligible] = useState(() =>
    canCertSignFromLocal(userId),
  );
  const [certUnlocked, setCertUnlocked] = useState(false);

  const isGateway = session.mode === "gateway" && Boolean(session.gatewayToken);
  const token = session.gatewayToken ?? "";
  /** Verified / root-cert users skip per-sign OTP for platform & SAMT modes. */
  const useCertUnlock =
    certSignEligible &&
    (session.mode === "in_platform" || session.mode === "samt_root_cert");

  useEffect(() => {
    setCertSignEligible(canCertSignFromLocal(userId));
    if (!userId) return;
    void fetchEsignProfile(apiBase)
      .then((p) => {
        setCertSignEligible(p.identityVerified || p.hasRootCertificate);
      })
      .catch(() => {
        /* keep local */
      });
  }, [apiBase, userId]);

  const refreshSession = useCallback(async () => {
    const next = await detectPendingSignature(apiBase, contractId);
    onSessionChange(next);
    if (!next) onComplete?.();
  }, [apiBase, contractId, onComplete, onSessionChange]);

  const loadGateway = useCallback(async () => {
    if (!isGateway || !token) return;
    setLoading(true);
    setError(null);
    try {
      const view = await fetchGatewayView(contractId, token);
      setGatewayView(view);
      if (view.signer.status === "pending" && view.processStatus === "active") {
        const seen = await markGatewaySeen(contractId, token);
        if (seen) setGatewayView(seen);
      }
      if (view.signer.status === "signed" || view.identityStep === "signed") {
        await refreshSession();
      }
    } catch (e) {
      setGatewayView(null);
      setError(e instanceof Error ? e.message : "بارگذاری درگاه امضا ناموفق بود");
    } finally {
      setLoading(false);
    }
  }, [contractId, isGateway, refreshSession, token]);

  useEffect(() => {
    if (isGateway) void loadGateway();
  }, [isGateway, loadGateway]);

  useEffect(() => {
    if (!isDesktop && session.canAct) {
      onMobileSheetOpenChange(true);
    }
  }, [isDesktop, onMobileSheetOpenChange, session.canAct]);

  const onGatewaySigned = useCallback(() => {
    void refreshSession();
  }, [refreshSession]);

  const onPlatformSeen = async () => {
    if (busy || session.signerStatus !== "pending") return;
    setBusy(true);
    setError(null);
    try {
      await actOnPlatformSignature(apiBase, contractId, session.processId, {
        action: "seen",
      });
      await refreshSession();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (
      !isGateway &&
      (session.mode === "in_platform" || session.mode === "samt_root_cert") &&
      session.signerStatus === "pending" &&
      session.canAct
    ) {
      void onPlatformSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.processId]);

  const onPlatformSign = async (opts?: { skipReauth?: boolean }) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (useCertUnlock && userId) {
        const rec = await loadBrowserCertificate(userId);
        if (rec) {
          const payload = new TextEncoder().encode(
            `nevisign-sign-v1:${contractId}:${session.processId}`,
          );
          await signWithBrowserCertificate(userId, payload);
        }
      }
      await actOnPlatformSignature(apiBase, contractId, session.processId, {
        action: "sign",
        reauthToken: opts?.skipReauth ? undefined : reauthToken ?? undefined,
      });
      setPlatformDone(true);
      await refreshSession();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا";
      if (!opts?.skipReauth && /reauth|احراز|OTP|کد/i.test(msg)) {
        const req = await requestPlatformSignReauth(
          apiBase,
          contractId,
          session.processId,
        );
        setReauthDebug(req.debugCode ?? null);
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const onVerifyReauth = async () => {
    if (busy || !reauthCode.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await verifyPlatformSignReauth(
        apiBase,
        contractId,
        session.processId,
        reauthCode.trim(),
      );
      setReauthToken(res.reauthToken ?? null);
      setReauthDebug(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  };

  const onPlatformReject = async () => {
    if (busy || !rejectReason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await actOnPlatformSignature(apiBase, contractId, session.processId, {
        action: "reject",
        rejectionReason: rejectReason.trim(),
      });
      setShowReject(false);
      await refreshSession();
    } catch (e) {
      setError(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusy(false);
    }
  };

  let panelBody: ReactNode;

  if (isGateway) {
    panelBody = loading ? (
      <p className="signing-muted">در حال بارگذاری درگاه امضا…</p>
    ) : error && !gatewayView ? (
      <p className="signing-error">{error}</p>
    ) : gatewayView ? (
      <SigningAuthPanel
        contractId={contractId}
        token={token}
        view={gatewayView}
        onViewChange={setGatewayView}
        onSigned={onGatewaySigned}
      />
    ) : null;
  } else if (session.mode === "gateway" && !session.gatewayToken) {
    panelBody = (
      <p className="signing-error">توکن درگاه امضا برای این سند در دسترس نیست.</p>
    );
  } else if (platformDone) {
    panelBody = (
      <div className="signing-success">
        <p className="signing-success-title">امضا ثبت شد</p>
      </div>
    );
  } else if (useCertUnlock && userId && !certUnlocked) {
    panelBody = (
      <CertUnlockPanel
        apiBase={apiBase}
        userId={userId}
        busy={busy}
        onUnlocked={() => {
          setCertUnlocked(true);
          void onPlatformSign({ skipReauth: true });
        }}
      />
    );
  } else {
    panelBody = (
      <div className="signing-auth-panel">
        <p className="signing-muted">
          {session.isMyStage
            ? useCertUnlock
              ? "امضای گواهی‌محور — سند آماده ثبت است."
              : "امضای درون‌سامانه — پس از مطالعهٔ سند، امضا را ثبت کنید."
            : "هنوز نوبت امضای شما نرسیده است."}
        </p>
        {error ? <p className="signing-error">{error}</p> : null}
        {reauthDebug ? (
          <p className="signing-debug">کد تأیید (توسعه): {reauthDebug}</p>
        ) : null}
        {!useCertUnlock && !reauthToken && session.canAct ? (
          <div className="signing-form">
            <label className="signing-field">
              کد تأیید امضا (در صورت نیاز)
              <input
                inputMode="numeric"
                value={reauthCode}
                onChange={(e) => setReauthCode(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="ghost-btn"
              disabled={busy}
              onClick={() => void onVerifyReauth()}
            >
              تأیید کد
            </button>
          </div>
        ) : null}
        {session.canAct && !showReject ? (
          <>
            <button
              type="button"
              className="primary-btn"
              disabled={busy}
              onClick={() => void onPlatformSign({ skipReauth: useCertUnlock })}
            >
              {busy ? "در حال ثبت…" : "ثبت امضا"}
            </button>
            <button
              type="button"
              className="ghost-btn"
              disabled={busy}
              onClick={() => setShowReject(true)}
            >
              رد امضا
            </button>
          </>
        ) : null}
        {showReject ? (
          <div className="signing-reject">
            <textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="دلیل رد"
            />
            <div className="signing-reject-actions">
              <button
                type="button"
                className="danger-btn"
                disabled={busy}
                onClick={() => void onPlatformReject()}
              >
                ثبت رد
              </button>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowReject(false)}
              >
                انصراف
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const panel = (
    <aside className="signing-side-panel side-panel" aria-label="امضای سند">
      <div className="signing-side-inner">
        <SigningPanelHeader />
        {panelBody}
      </div>
    </aside>
  );

  return (
    <>
      {isDesktop ? panel : null}

      {!isDesktop ? (
        <>
          <button
            type="button"
            className="signing-float-btn"
            aria-label="امضا"
            onClick={() => onMobileSheetOpenChange(true)}
          >
            <PenLine size={20} />
            <span>امضا</span>
          </button>

          {mobileSheetOpen ? (
            <div
              className="signing-sheet-backdrop"
              role="presentation"
              onClick={() => onMobileSheetOpenChange(false)}
            />
          ) : null}

          <div
            className={`signing-sheet${mobileSheetOpen ? " open" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label="احراز هویت و امضا"
          >
            <div className="signing-sheet-handle" aria-hidden />
            <div className="signing-sheet-head">
              <SigningPanelHeader />
              <button
                type="button"
                className="icon-btn"
                aria-label="بستن"
                onClick={() => onMobileSheetOpenChange(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="signing-sheet-body">{panelBody}</div>
          </div>
        </>
      ) : null}
    </>
  );
}
